import React from "react";
import { Flag } from "lucide-react";

/**
 * Red-flag summary panel for the results screen.
 * Lists all questions where the user picked the WORST-outcome option.
 *
 * Props:
 *   questions: array of { code|num, label|prompt, options:[{score, label}] }
 *   answers:   map of questionKey → option idx (single-select)
 *              OR map of questionKey → [array of idxs] (multi-select)
 *   direction: "maturity" (worst = lowest score) | "risk" (worst = highest score)
 *   questionKeyFn: optional fn(question) → key for lookup; defaults to q.code or q.num
 *   testidPrefix: data-testid prefix
 */
export default function RedFlagSummary({
  questions,
  answers,
  direction = "maturity",
  questionKeyFn,
  testidPrefix = "redflag",
}) {
  const getKey = questionKeyFn || ((q) => String(q.code ?? q.num));
  const flags = [];
  for (const q of questions || []) {
    const key = getKey(q);
    const ans = answers?.[key];
    if (ans == null) continue;
    const scores = (q.options || []).map((o, i) => (typeof o?.score === "number" ? o.score : i));
    if (scores.length === 0) continue;
    const worstScore = direction === "risk" ? Math.max(...scores) : Math.min(...scores);
    // Single-select
    if (typeof ans === "number") {
      if (scores[ans] === worstScore) {
        flags.push({ q, idx: ans });
      }
    } else if (Array.isArray(ans)) {
      // Multi-select: flag if ANY selected option is the worst
      for (const i of ans) {
        if (scores[i] === worstScore) {
          flags.push({ q, idx: i });
          break;
        }
      }
    }
  }

  if (flags.length === 0) {
    return (
      <div className="card-flat p-5 bg-good-soft/30 border-l-4 border-good" data-testid={`${testidPrefix}-none`}>
        <div className="font-display font-semibold text-good text-sm">No red flags · No worst-outcome answers selected.</div>
      </div>
    );
  }

  return (
    <div className="card-flat p-5 border-l-4 border-red-400" data-testid={`${testidPrefix}-panel`}>
      <div className="flex items-center gap-2 mb-3">
        <Flag className="w-4 h-4 text-red-600" />
        <div className="font-display font-semibold text-red-700">Red Flags · {flags.length} question{flags.length > 1 ? "s" : ""} need review</div>
      </div>
      <div className="text-[11px] text-ink-muted uppercase tracking-wider mb-3">
        {direction === "risk" ? "Highest-risk option selected" : "Lowest-scoring option selected"}
      </div>
      <ul className="space-y-2.5">
        {flags.map(({ q, idx }) => (
          <li key={getKey(q)} className="flex items-start gap-3 text-sm" data-testid={`${testidPrefix}-q-${getKey(q)}`}>
            <span className="shrink-0 font-num text-[10px] text-red-600 bg-red-50 border border-red-200 rounded-sm px-1.5 py-0.5 mt-0.5">{getKey(q)}</span>
            <div className="flex-1 min-w-0">
              <div className="font-medium leading-snug">{q.label || q.prompt}</div>
              <div className="text-[12px] text-red-700 mt-0.5">→ {q.options[idx]?.label || `Option ${idx + 1}`}</div>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
